import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import SeoPageComponent from '../../components/SeoPageComponent/SeoPageComponent';
import './DirectAwards.css';
import { fetchDirectAwardSearchResults } from '../../services/publicTendersApi';
import AuctionList from '../../components/AuctionList/AuctionList';
import logo from '../../assets/icon.png';
import Breadcrumbs from '../../components/Breadcrumbs';
import ListLoadingHolder from '../../components/LoadingHolders/ListLoadingHolder';
import directAwardCounties from './directAwardCounties';
import directAwardFilterLabels from './directAwardFilterLabels';

const DirectAwards = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize filters from URL parameters
  const getInitialFilters = () => ({
    auctionId: searchParams.get('auctionId') || '',
    auctionTitle: searchParams.get('auctionTitle') || '',
    publishedDate: searchParams.get('publishedDate') || '',
    cpvCode: searchParams.get('cpvCode') || '',
    authorityName: searchParams.get('authorityName') || '',
    authorityCUI: searchParams.get('authorityCUI') || '',
    city: searchParams.get('city') || '',
    county: searchParams.get('county') || '',
    minEstimatedValue: searchParams.get('minEstimatedValue') || '',
    maxEstimatedValue: searchParams.get('maxEstimatedValue') || '',
    currency: searchParams.get('currency') || '',
    pageIndex: parseInt(searchParams.get('pageIndex') || '0'),
    pageSize: parseInt(searchParams.get('pageSize') || '10'),
  });

  const [filters, setFilters] = useState(getInitialFilters());
  const [errors, setErrors] = useState([]);
  const [results, setResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false); // ✅ Starea pentru dropdown-ul filtrelor

  // Update URL when filters change
  useEffect(() => {
    const params = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (
        value !== '' &&
        value !== null &&
        value !== undefined &&
        value !== 0
      ) {
        // Don't include pageIndex: 0 or pageSize: 10 (defaults)
        if (
          (key === 'pageIndex' && value === 0) ||
          (key === 'pageSize' && value === 10)
        ) {
          return;
        }
        params[key] = value.toString();
      }
    });
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  const validateValues = useCallback(() => {
    const validationErrors = [];
    if (parseFloat(filters.minEstimatedValue) < 0) {
      validationErrors.push('Valoarea estimată minimă nu poate fi negativă.');
    }
    if (parseFloat(filters.maxEstimatedValue) < 0) {
      validationErrors.push('Valoarea estimată maximă nu poate fi negativă.');
    }
    if (
      parseFloat(filters.maxEstimatedValue) <
      parseFloat(filters.minEstimatedValue)
    ) {
      validationErrors.push(
        'Valoarea estimată minimă nu poate fi mai mare decât valoarea maximă.',
      );
    }
    setErrors(validationErrors);
    return validationErrors.length === 0;
  }, [filters]);

  // Auto-fetch when filters change
  useEffect(() => {
    let cancelled = false;
    if (!validateValues()) return;
    setLoading(true);
    fetchDirectAwardSearchResults(filters)
      .then((data) => {
        if (cancelled) return;
        setResults(data.results);
        setTotalResults(data.totalResults);
      })
      .catch((error) => {
        if (!cancelled) console.error('Error fetching search results:', error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleFilter = useCallback(() => {
    // no-op: filter changes drive fetches via the effect above
  }, []);

  useEffect(() => {
    if (window.prerenderReady !== undefined) {
      window.prerenderReady = true;
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
      pageIndex: 0, // Resetăm pagina la 0 când se schimbă orice filtru
    }));
  };

  const handleReset = () => {
    setFilters({
      auctionId: '',
      auctionTitle: '',
      publishedDate: '',
      cpvCode: '',
      authorityName: '',
      authorityCUI: '',
      city: '',
      county: '',
      minEstimatedValue: '',
      maxEstimatedValue: '',
      currency: '',
      pageIndex: 0,
      pageSize: 10,
    });
    setErrors([]);
    setResults([]);
    setTotalResults(0);
    handleFilter();
  };

  const breadcrumbLinks = [
    { href: '/', label: 'Home' },
    { label: 'Achizitii Directe' },
  ];

  const pageTitle = 'Achiziții Directe - Licitatii-Constructii';
  const pageDescription =
    'Descoperă cele mai recente achiziții directe pentru proiecte de construcții.';
  const pageUrl = `${import.meta.env.VITE_DOMAIN}/achizitii-directe`;

  // Build canonical URL (exclude pageIndex to avoid duplicate content penalties)
  const canonicalUrl = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      // Exclude pageIndex and default pageSize from canonical URL
      if (key === 'pageIndex') return;
      if (key === 'pageSize' && value === 10) return;

      if (
        value !== '' &&
        value !== null &&
        value !== undefined &&
        value !== 0
      ) {
        params.append(key, value.toString());
      }
    });

    const queryString = params.toString();
    return `${window.location.origin}/achizitii-directe${queryString ? '?' + queryString : ''}`;
  }, [filters]);

  return (
    <>
      <SeoPageComponent
        pageTitle={pageTitle}
        pageDescription={pageDescription}
        pageUrl={pageUrl}
        canonicalUrl={canonicalUrl}
        logoUrl={logo}
      />
      <section className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-green-100 py-12">
        <div className="container max-w-5xl mx-auto">
          <Breadcrumbs breadcrumbLinks={breadcrumbLinks} />
          <h1 className="text-4xl md:text-5xl font-extrabold text-indigo-700 mb-4 text-center">
            Achiziții directe de construcții - lucrări, servicii, furnizare de
            produse
          </h1>

          <div className="bg-white rounded-2xl shadow-2xl p-8 border-t-8 border-indigo-500 mb-10 animate-fade-in">
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="w-full bg-gradient-to-r from-indigo-500 to-green-500 text-white px-4 py-3 rounded-xl font-bold text-lg hover:from-indigo-600 hover:to-green-600 transition-all flex justify-between items-center mb-4"
            >
              {isFilterOpen ? 'Ascunde filtrele ▲' : 'Afișează filtrele ▼'}
            </button>
            {isFilterOpen && (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-4">
                {Object.keys(filters)
                  .filter(
                    (filter) => filter !== 'pageIndex' && filter !== 'pageSize',
                  )
                  .map((filter) =>
                    filter === 'county' ? (
                      <div key={filter}>
                        <label className="block text-gray-700 font-semibold mb-1">
                          {directAwardFilterLabels[filter]?.label || filter}
                        </label>
                        <select
                          name={filter}
                          value={filters[filter]}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none"
                        >
                          <option value="">
                            {directAwardFilterLabels[filter]?.placeholder ||
                              'Selectează'}
                          </option>
                          {directAwardCounties.map((county) => (
                            <option key={county.value} value={county.value}>
                              {county.text}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div key={filter}>
                        <label className="block text-gray-700 font-semibold mb-1">
                          {directAwardFilterLabels[filter]?.label || filter}
                        </label>
                        <input
                          type={
                            filter.includes('Date')
                              ? 'date'
                              : filter.includes('Value')
                                ? 'number'
                                : 'text'
                          }
                          name={filter}
                          value={filters[filter]}
                          onChange={handleInputChange}
                          placeholder={
                            directAwardFilterLabels[filter]?.placeholder || ''
                          }
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none"
                        />
                      </div>
                    ),
                  )}
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 animate-fade-in">
            <div className="flex items-center justify-end space-x-4 border-b pb-5 mb-8">
              <button
                onClick={() => handleFilter()}
                className="bg-gradient-to-r from-indigo-500 to-green-500 text-white px-6 py-2 rounded-xl font-bold hover:from-indigo-600 hover:to-green-600 transition-all"
              >
                Filtrează
              </button>
              <button
                onClick={handleReset}
                className="bg-red-100 hover:bg-red-300 text-gray-700 px-6 py-2 rounded-xl font-bold hover:text-white duration-300"
              >
                Resetează
              </button>
            </div>

            <div className="mt-8">
              {loading ? (
                <ListLoadingHolder />
              ) : (
                <AuctionList
                  auctions={results}
                  totalResults={totalResults}
                  pageSize={filters.pageSize}
                  pageIndex={filters.pageIndex}
                  auctionType={'DirectAwards'}
                />
              )}
            </div>

            {/* PAGINARE DOAR JOS */}
            {!loading && totalResults > 0 && (
              <div className="flex flex-wrap items-center justify-center gap-2 mt-10">
                <button
                  onClick={() =>
                    setFilters((prev) => ({ ...prev, pageIndex: 0 }))
                  }
                  disabled={filters.pageIndex === 0}
                  className="px-3 py-2 rounded-lg font-bold bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition disabled:opacity-50"
                >
                  Prima
                </button>
                <button
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      pageIndex: prev.pageIndex - 1,
                    }))
                  }
                  disabled={filters.pageIndex === 0}
                  className="px-3 py-2 rounded-lg font-bold bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition disabled:opacity-50"
                >
                  &laquo;
                </button>
                {Array.from(
                  {
                    length: Math.min(
                      5,
                      Math.ceil(totalResults / filters.pageSize),
                    ),
                  },
                  (_, i) => {
                    let startPage = Math.max(
                      0,
                      Math.min(
                        filters.pageIndex - 2,
                        Math.ceil(totalResults / filters.pageSize) - 5,
                      ),
                    );
                    let page = startPage + i;
                    if (
                      page < 0 ||
                      page >= Math.ceil(totalResults / filters.pageSize)
                    )
                      return null;
                    return (
                      <button
                        key={page}
                        onClick={() =>
                          setFilters((prev) => ({ ...prev, pageIndex: page }))
                        }
                        className={`px-3 py-2 rounded-lg font-bold ${
                          filters.pageIndex === page
                            ? 'bg-indigo-500 text-white'
                            : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                        } transition`}
                      >
                        {page + 1}
                      </button>
                    );
                  },
                )}
                <button
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      pageIndex: prev.pageIndex + 1,
                    }))
                  }
                  disabled={
                    filters.pageIndex >=
                    Math.ceil(totalResults / filters.pageSize) - 1
                  }
                  className="px-3 py-2 rounded-lg font-bold bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition disabled:opacity-50"
                >
                  &raquo;
                </button>
                <button
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      pageIndex: Math.max(
                        0,
                        Math.ceil(totalResults / filters.pageSize) - 1,
                      ),
                    }))
                  }
                  disabled={
                    filters.pageIndex >=
                    Math.ceil(totalResults / filters.pageSize) - 1
                  }
                  className="px-3 py-2 rounded-lg font-bold bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition disabled:opacity-50"
                >
                  Ultima
                </button>
                <label className="ml-4 text-gray-700 text-sm flex items-center gap-2">
                  / pagină
                  <select
                    value={filters.pageSize}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        pageSize: Number(e.target.value),
                        pageIndex: 0,
                      }))
                    }
                    className="ml-1 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-400 outline-none bg-indigo-50"
                  >
                    {[10, 20, 50, 100].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
                <span className="ml-4 text-gray-500 text-sm">
                  Pagina{' '}
                  {Math.min(
                    filters.pageIndex + 1,
                    Math.max(1, Math.ceil(totalResults / filters.pageSize)),
                  )}{' '}
                  din {Math.max(1, Math.ceil(totalResults / filters.pageSize))}
                </span>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default DirectAwards;
